<template>
  <div class="console-head">
    <span class="nerv-section-label">Sec 01.5 // Command console</span>
    <span class="nerv-status" :class="running ? 'nerv-lcd' : 'nerv-orange'">
      {{ running ? 'All systems nominal' : 'Boot sequence running' }}
    </span>
  </div>

  <div class="nerv-bezel">
    <div ref="screen" class="nerv-screen console-screen">
      <!-- Slow horizontal scan, pure CSS. -->
      <div class="console-scan" aria-hidden="true"></div>

      <div class="console-grid">
        <!-- Area scan radar -->
        <div class="console-cell">
          <span class="nerv-data-label">Area scan</span>
          <div ref="radarBox" class="radar-box" aria-hidden="true">
            <canvas ref="radarStatic" class="layer"></canvas>
            <canvas ref="radarDyn" class="layer"></canvas>
            <span ref="bearingEl" class="radar-bearing nerv-caption">BRG 000</span>
          </div>
          <span class="nerv-caption cell-note">
            {{ reduced ? 'Sweep parked, motion reduced per system setting' : 'Passive sweep, decorative' }}
          </span>
        </div>

        <!-- Refresh trace + event log -->
        <div class="console-cell scope-cell">
          <span class="nerv-data-label">Refresh trace</span>
          <div ref="scopeBox" class="scope-box" aria-hidden="true">
            <canvas ref="scopeCanvas" class="layer"></canvas>
          </div>
          <span class="nerv-caption cell-note">
            min {{ histMin }} / avg {{ histAvg }} / max {{ histMax }} fps, measured live
          </span>

          <div class="boot-log" aria-hidden="true">
            <p v-for="(line, i) in visibleLines" :key="i" class="boot-line">{{ line }}</p>
            <p v-if="!running" class="boot-line">
              <span class="animate-cursor-blink">_</span>
            </p>
          </div>
        </div>

        <!-- Station readout -->
        <div class="console-cell">
          <span class="nerv-data-label">Station readout</span>
          <dl class="console-rows">
            <div v-for="row in rows" :key="row.key" class="nerv-datarow">
              <dt class="nerv-datarow-key">{{ row.key }}</dt>
              <dd class="nerv-datarow-value">{{ row.value }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Oversized live numerals -->
      <div class="console-big">
        <NervReadout label="Refresh" :value="t.fps" unit=" fps" tone="green" note="Frames rendered by your browser" />
        <NervReadout label="Session" :value="t.uptime" note="Time on station" />
        <NervReadout
          v-if="t.heapUsedMb !== null"
          label="JS heap"
          :value="t.heapUsedMb.toFixed(1)"
          unit=" MB"
          tone="cyan"
          note="Live from this tab"
        />
        <NervReadout
          v-else-if="t.rtt !== null"
          label="Round trip"
          :value="t.rtt"
          unit=" ms"
          tone="cyan"
          note="Reported by your browser"
        />
        <NervReadout
          v-else
          label="Viewport"
          :value="`${t.viewportW} x ${t.viewportH}`"
          tone="cyan"
          note="Current window"
        />
      </div>

      <!-- The tracking reticle moved to NervReticle, mounted page-level in
           App.vue, so the targeting axis runs across the whole site rather
           than only inside this panel. -->
    </div>
  </div>

  <p class="nerv-microcopy console-note">
    Every number on this console is measured in your browser, on this device, and rendered
    here. Nothing is sent, stored or logged. The radar is decorative; the readings are not.
  </p>
</template>

<script lang="ts">
import { defineComponent, computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import NervReadout from '@/components/NervReadout.vue';
import { acquireTelemetry, releaseTelemetry, TelemetryState } from '@/composables/useTelemetry';

const CYAN = '#00f6ff';
const GREEN = '#39ff14';
const RED = '#ff2b1d';

interface Blip {
  a: number; // bearing, radians
  r: number; // radius, 0..1 of scan radius
  drift: number;
  hostile: boolean;
  intensity: number;
}

export default defineComponent({
  name: 'NervConsole',
  components: { NervReadout },
  setup() {
    const t = acquireTelemetry();

    const screen = ref<HTMLElement | null>(null);
    const radarBox = ref<HTMLElement | null>(null);
    const radarStatic = ref<HTMLCanvasElement | null>(null);
    const radarDyn = ref<HTMLCanvasElement | null>(null);
    const scopeBox = ref<HTMLElement | null>(null);
    const scopeCanvas = ref<HTMLCanvasElement | null>(null);
    const bearingEl = ref<HTMLElement | null>(null);

    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const running = ref(reduced);
    const visibleLines = reactive<string[]>([]);

    const histMin = computed(() => (t.fpsHistory.length ? Math.min(...t.fpsHistory) : 0));
    const histMax = computed(() => (t.fpsHistory.length ? Math.max(...t.fpsHistory) : 0));
    const histAvg = computed(() => {
      if (!t.fpsHistory.length) return 0;
      return Math.round(t.fpsHistory.reduce((a, b) => a + b, 0) / t.fpsHistory.length);
    });

    const na = 'Unavailable';
    const rows = computed(() => {
      const out: Array<{ key: string; value: string }> = [
        { key: 'GPU', value: t.gpu || na },
        { key: 'Platform', value: t.platform || na },
        { key: 'CPU cores', value: t.cores === null ? na : String(t.cores) },
      ];
      if (t.memoryGb !== null) out.push({ key: 'Device memory', value: `${t.memoryGb} GB` });
      out.push(
        { key: 'Engine', value: t.engine || na },
        { key: 'Time zone', value: t.timezone || na },
        { key: 'Viewport', value: `${t.viewportW} x ${t.viewportH} @ ${t.dpr}x` },
        {
          key: 'Link',
          value: t.netType
            ? `${t.netType.toUpperCase()}${t.downlink !== null ? ` / ${t.downlink.toFixed(1)} Mb/s` : ''}`
            : t.online
              ? 'Online'
              : 'Offline',
        },
        { key: 'Page load', value: t.loadMs === null ? 'In progress' : `${t.loadMs} ms` },
      );
      if (t.ttfbMs !== null) out.push({ key: 'First byte', value: `${t.ttfbMs} ms` });
      if (t.batteryLevel !== null) {
        out.push({
          key: 'Battery',
          value: `${t.batteryLevel}%${t.batteryCharging ? ' charging' : ''}`,
        });
      }
      return out;
    });

    // --- non-reactive animation state ------------------------------------
    let rafId = 0;
    let lastFrame = 0;
    let sweep = 0; // radians
    let instFps = 60;
    let lastBearingShown = -1;
    const bootTimers: number[] = [];
    let ro: ResizeObserver | null = null;

    // The sweep canvas holds the phosphor trail, and sizing a canvas clears it.
    // Track the applied size so it is only re-fitted when the box or the pixel
    // ratio actually changes, rather than every frame.
    let dynSize = 0;
    let dynDpr = 0;


    const blips: Blip[] = [
      { a: 0.6, r: 0.52, drift: 0.9, hostile: false, intensity: 0 },
      { a: 1.5, r: 0.81, drift: -0.6, hostile: false, intensity: 0 },
      { a: 2.2, r: 0.34, drift: 0.4, hostile: false, intensity: 0 },
      { a: 2.9, r: 0.66, drift: 1.2, hostile: true, intensity: 0 },
      { a: 3.8, r: 0.47, drift: -0.8, hostile: false, intensity: 0 },
      { a: 4.4, r: 0.9, drift: 0.5, hostile: false, intensity: 0 },
      { a: 5.1, r: 0.28, drift: -0.3, hostile: false, intensity: 0 },
      { a: 5.9, r: 0.72, drift: 0.7, hostile: false, intensity: 0 },
    ];

    function fitCanvas(canvas: HTMLCanvasElement, w: number, h: number) {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return ctx;
    }

    // --- radar -----------------------------------------------------------
    function drawRadarStatic() {
      const box = radarBox.value;
      const canvas = radarStatic.value;
      if (!box || !canvas) return;
      const s = box.clientWidth;
      if (!s) return;
      const ctx = fitCanvas(canvas, s, s);
      if (!ctx) return;
      const c = s / 2;
      const R = c - 6;

      ctx.clearRect(0, 0, s, s);

      // Hex field, clipped to the scan circle.
      ctx.save();
      ctx.beginPath();
      ctx.arc(c, c, R, 0, Math.PI * 2);
      ctx.clip();
      ctx.strokeStyle = 'rgba(0, 246, 255, 0.07)';
      ctx.lineWidth = 1;
      const hr = Math.max(12, s / 16);
      const hh = hr * Math.sqrt(3) / 2;
      for (let row = -1; row * hh * 2 < s + hh * 2; row++) {
        for (let col = -1; col * hr * 1.5 < s + hr; col++) {
          const x = col * hr * 1.5;
          const y = row * hh * 2 + (col % 2 ? hh : 0);
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const ang = (Math.PI / 3) * i;
            const px = x + hr * Math.cos(ang);
            const py = y + hr * Math.sin(ang);
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.stroke();
        }
      }
      ctx.restore();

      // Range rings and cross lines.
      ctx.strokeStyle = 'rgba(255, 153, 0, 0.22)';
      for (const f of [0.33, 0.66, 1]) {
        ctx.beginPath();
        ctx.arc(c, c, R * f, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.strokeStyle = 'rgba(255, 153, 0, 0.12)';
      ctx.beginPath();
      ctx.moveTo(c - R, c);
      ctx.lineTo(c + R, c);
      ctx.moveTo(c, c - R);
      ctx.lineTo(c, c + R);
      ctx.stroke();

      // Bearing ticks every 15 degrees, longer every 45.
      for (let deg = 0; deg < 360; deg += 15) {
        const rad = (deg * Math.PI) / 180;
        const major = deg % 45 === 0;
        const inner = R - (major ? 8 : 4);
        ctx.strokeStyle = major ? 'rgba(255, 153, 0, 0.4)' : 'rgba(255, 153, 0, 0.2)';
        ctx.beginPath();
        ctx.moveTo(c + inner * Math.cos(rad), c + inner * Math.sin(rad));
        ctx.lineTo(c + R * Math.cos(rad), c + R * Math.sin(rad));
        ctx.stroke();
      }
    }

    function drawRadarSweep(dt: number) {
      const box = radarBox.value;
      const canvas = radarDyn.value;
      if (!box || !canvas) return;
      const s = box.clientWidth;
      if (!s) return;

      // Without this the backing store stays at the canvas default of 300x150
      // while CSS stretches it square, so the sweep is drawn in a different
      // coordinate space from the rings on the static layer and does not
      // originate from the centre. Re-fit only on a real size change, since
      // fitCanvas clears the trail.
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      let ctx: CanvasRenderingContext2D | null;
      if (s !== dynSize || dpr !== dynDpr) {
        ctx = fitCanvas(canvas, s, s);
        dynSize = s;
        dynDpr = dpr;
      } else {
        ctx = canvas.getContext('2d');
      }
      if (!ctx) return;
      const c = s / 2;
      const R = c - 6;

      // Fade what is already there toward transparent: this is the phosphor
      // afterglow. destination-out erases alpha instead of painting black
      // over the static layer beneath.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.07)';
      ctx.fillRect(0, 0, s, s);
      ctx.globalCompositeOperation = 'source-over';

      const prev = sweep;
      const advance = dt * 0.00105;
      sweep = (sweep + advance) % (Math.PI * 2);

      // Fill the arc crossed since the last frame with intermediate lines so
      // the wedge stays continuous when the frame rate dips; otherwise slow
      // frames leave visible spokes.
      const steps = Math.min(40, Math.max(1, Math.ceil(advance / 0.012)));
      ctx.lineWidth = 2;
      for (let i = 1; i < steps; i++) {
        const a = prev + (advance * i) / steps;
        ctx.strokeStyle = 'rgba(255, 153, 0, 0.35)';
        ctx.beginPath();
        ctx.moveTo(c, c);
        ctx.lineTo(c + R * Math.cos(a), c + R * Math.sin(a));
        ctx.stroke();
      }

      // Sweep head, bright toward the rim.
      const grad = ctx.createLinearGradient(c, c, c + R * Math.cos(sweep), c + R * Math.sin(sweep));
      grad.addColorStop(0, 'rgba(255, 153, 0, 0.05)');
      grad.addColorStop(1, 'rgba(255, 153, 0, 0.85)');
      ctx.strokeStyle = grad;
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.lineTo(c + R * Math.cos(sweep), c + R * Math.sin(sweep));
      ctx.stroke();

      // Contacts light up as the sweep passes and decay after.
      for (const b of blips) {
        b.a = (b.a + dt * 0.00001 * b.drift + Math.PI * 2) % (Math.PI * 2);
        let diff = Math.abs(sweep - b.a);
        if (diff > Math.PI) diff = Math.PI * 2 - diff;
        if (diff < 0.06) b.intensity = 1;
        b.intensity *= Math.pow(0.55, dt / 1000);
        if (b.intensity < 0.02) continue;
        const x = c + R * b.r * Math.cos(b.a);
        const y = c + R * b.r * Math.sin(b.a);
        ctx.save();
        ctx.globalAlpha = b.intensity;
        ctx.shadowBlur = 8;
        ctx.shadowColor = b.hostile ? RED : CYAN;
        ctx.fillStyle = b.hostile ? RED : CYAN;
        const d = b.hostile ? 5 : 4;
        ctx.fillRect(x - d / 2, y - d / 2, d, d);
        if (b.hostile && b.intensity > 0.4) {
          ctx.strokeStyle = RED;
          ctx.lineWidth = 1;
          ctx.strokeRect(x - 6, y - 6, 12, 12);
        }
        ctx.restore();
      }

      const deg = Math.round((sweep * 180) / Math.PI) % 360;
      const shown = deg - (deg % 3);
      if (shown !== lastBearingShown && bearingEl.value) {
        lastBearingShown = shown;
        bearingEl.value.textContent = `BRG ${String(shown).padStart(3, '0')}`;
      }
    }

    // A single still frame for prefers-reduced-motion: sweep parked, all
    // contacts faintly lit.
    function drawRadarStill() {
      const box = radarBox.value;
      const canvas = radarDyn.value;
      if (!box || !canvas) return;
      const s = box.clientWidth;
      if (!s) return;
      const ctx = fitCanvas(canvas, s, s);
      if (!ctx) return;
      // Keep the sweep path's size tracking in step, so switching motion
      // preference mid-session does not leave a stale backing store.
      dynSize = s;
      dynDpr = Math.min(2, window.devicePixelRatio || 1);
      const c = s / 2;
      const R = c - 6;
      ctx.clearRect(0, 0, s, s);
      ctx.strokeStyle = 'rgba(255, 153, 0, 0.6)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.lineTo(c + R * Math.cos(-0.9), c + R * Math.sin(-0.9));
      ctx.stroke();
      for (const b of blips) {
        const x = c + R * b.r * Math.cos(b.a);
        const y = c + R * b.r * Math.sin(b.a);
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = b.hostile ? RED : CYAN;
        ctx.fillRect(x - 2, y - 2, 4, 4);
      }
      ctx.globalAlpha = 1;
    }

    // --- refresh trace ---------------------------------------------------
    function drawScope() {
      const box = scopeBox.value;
      const canvas = scopeCanvas.value;
      if (!box || !canvas) return;
      const w = box.clientWidth;
      const h = box.clientHeight;
      if (!w || !h) return;
      const ctx = fitCanvas(canvas, w, h);
      if (!ctx) return;

      ctx.clearRect(0, 0, w, h);

      // Grid: horizontal lines at 30 fps steps, vertical every 10 samples.
      ctx.strokeStyle = 'rgba(255, 153, 0, 0.1)';
      ctx.lineWidth = 1;
      for (let fps = 30; fps <= 120; fps += 30) {
        const y = h - (fps / 130) * h;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
      const cols = 9;
      for (let i = 1; i < cols; i++) {
        const x = (w / cols) * i;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }

      const hist = t.fpsHistory;
      const points: number[] = hist.slice(-60);
      points.push(Math.round(instFps));
      const n = points.length;
      if (n < 2) return;
      const step = w / (n - 1);
      const yFor = (v: number) => h - (Math.max(0, Math.min(130, v)) / 130) * h;

      // Fill under the trace.
      ctx.beginPath();
      ctx.moveTo(0, h);
      for (let i = 0; i < n; i++) ctx.lineTo(i * step, yFor(points[i]));
      ctx.lineTo(w, h);
      ctx.closePath();
      ctx.fillStyle = 'rgba(57, 255, 20, 0.07)';
      ctx.fill();

      // The trace itself, phosphor green.
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const x = i * step;
        const y = yFor(points[i]);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = GREEN;
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 9;
      ctx.shadowColor = GREEN;
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Live head.
      ctx.fillStyle = GREEN;
      ctx.fillRect(w - 3, yFor(points[n - 1]) - 1.5, 3, 3);
    }

    // --- boot sequence ---------------------------------------------------
    function fmt(v: string | number | null, fallback = 'UNAVAILABLE'): string {
      return v === null || v === '' ? fallback : String(v).toUpperCase();
    }

    function scheduleBoot() {
      const lines: Array<() => string> = [
        () => 'DEV1 COMMAND CONSOLE',
        () => '> INIT DISPLAY .......... OK',
        () => `> GPU ${fmt(t.gpu && t.gpu.length > 46 ? `${t.gpu.slice(0, 46)}...` : t.gpu)}`,
        () =>
          `> PLATFORM ${fmt(t.platform)} / ${t.cores === null ? '?' : t.cores} CORES${
            t.memoryGb === null ? '' : ` / ${t.memoryGb} GB`
          }`,
        () =>
          t.netType
            ? `> LINK ${fmt(t.netType)}${t.downlink === null ? '' : ` / ${t.downlink.toFixed(1)} MB/S`}${
                t.rtt === null ? '' : ` / RTT ${t.rtt} MS`
              }`
            : `> LINK ${t.online ? 'ONLINE' : 'OFFLINE'}`,
        () =>
          `> PAGE LOAD ${t.loadMs === null ? 'IN PROGRESS' : `${t.loadMs} MS`}${
            t.ttfbMs === null ? '' : ` / TTFB ${t.ttfbMs} MS`
          }`,
        () => '> RETICLE TRACK ......... OK',
        () => '> ALL SYSTEMS NOMINAL',
      ];
      lines.forEach((make, i) => {
        bootTimers.push(
          window.setTimeout(() => {
            visibleLines.push(make());
            if (i === lines.length - 1) running.value = true;
          }, 260 + i * 340),
        );
      });
    }

    // --- main loop -------------------------------------------------------
    function frame(now: number) {
      const dt = Math.min(100, now - lastFrame || 16);
      lastFrame = now;
      if (dt > 0) instFps = instFps * 0.92 + (1000 / dt) * 0.08;
      drawRadarSweep(dt);
      drawScope();
      rafId = requestAnimationFrame(frame);
    }

    // Under reduced motion there is no animation loop, but the trace should
    // still reflect the data as it arrives: redraw once per 1 Hz sample.
    if (reduced) {
      watch(
        () => t.fps,
        () => {
          instFps = t.fps || 60;
          drawScope();
        },
      );
    }

    onMounted(() => {
      drawRadarStatic();
      if (reduced) {
        // No motion: reveal the log at once, park the sweep, draw one trace.
        instFps = t.fps || 60;
        drawRadarStill();
        drawScope();
        scheduleBootInstant();
      } else {
        scheduleBoot();
        lastFrame = performance.now();
        rafId = requestAnimationFrame(frame);
      }
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => {
          drawRadarStatic();
          if (reduced) {
            drawRadarStill();
            drawScope();
          }
        });
        if (radarBox.value) ro.observe(radarBox.value);
        if (scopeBox.value) ro.observe(scopeBox.value);
      }
    });

    function scheduleBootInstant() {
      visibleLines.push(
        'DEV1 COMMAND CONSOLE',
        '> INIT DISPLAY .......... OK',
        `> GPU ${fmt(t.gpu)}`,
        `> PLATFORM ${fmt(t.platform)}`,
        '> ALL SYSTEMS NOMINAL',
      );
      running.value = true;
    }

    onBeforeUnmount(() => {
      cancelAnimationFrame(rafId);
      bootTimers.forEach((id) => window.clearTimeout(id));
      if (ro) ro.disconnect();
      releaseTelemetry();
    });

    return {
      t: t as TelemetryState,
      screen,
      radarBox,
      radarStatic,
      radarDyn,
      scopeBox,
      scopeCanvas,
      bearingEl,
      reduced,
      running,
      visibleLines,
      histMin,
      histMax,
      histAvg,
      rows,
    };
  },
});
</script>

<style scoped>
.console-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-block-end: 0.75rem;
}

.console-screen {
  overflow: hidden;
}

/* A faint scanline drifting down the whole console, on loop. */
.console-scan {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 56px;
  pointer-events: none;
  background: linear-gradient(180deg, transparent, rgba(255, 153, 0, 0.045), transparent);
  animation: console-scan-drift 7s linear infinite;
}

@keyframes console-scan-drift {
  0% { transform: translateY(-80px); }
  100% { transform: translateY(min(60vh, 640px)); }
}

.console-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.2fr) minmax(0, 0.95fr);
  gap: clamp(1rem, 2.5vw, 2.25rem);
}

@media (max-width: 980px) {
  .console-grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .scope-cell {
    grid-column: 1 / -1;
    order: 3;
  }
}

@media (max-width: 620px) {
  .console-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.console-cell {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
}

.cell-note {
  color: var(--text-faint);
}

.radar-box {
  position: relative;
  width: 100%;
  max-width: 300px;
  aspect-ratio: 1;
  margin-block-start: 0.25rem;
}

.layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.radar-bearing {
  position: absolute;
  right: 0.25rem;
  bottom: 0.25rem;
  color: var(--nerv-orange);
  font-variant-numeric: tabular-nums;
}

.scope-box {
  position: relative;
  width: 100%;
  height: 118px;
  border: 1px solid var(--line-soft);
  background: rgba(0, 0, 0, 0.5);
}

.boot-log {
  margin-block-start: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--line-soft);
  background: rgba(0, 0, 0, 0.45);
  min-height: 10.2em;
}

.boot-line {
  color: var(--nerv-lcd-green);
  font-family: var(--font-nerv-mono);
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  line-height: 1.75;
  text-shadow: 0 0 6px rgba(57, 255, 20, 0.35);
  overflow-wrap: anywhere;
}

.console-rows {
  margin-block-start: 0.25rem;
}

.console-rows .nerv-datarow {
  flex-wrap: wrap;
  padding-block: 0.4rem;
}

.console-rows .nerv-datarow-value {
  font-size: 0.72rem;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.console-big {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(1rem, 2.5vw, 2.25rem);
  margin-block-start: clamp(1.25rem, 2.5vw, 2rem);
  padding-block-start: clamp(1rem, 2vw, 1.5rem);
  border-top: 1px solid var(--line-soft);
}

.console-big :deep(.nerv-readout-value) {
  font-size: clamp(1.9rem, 3.6vw, 3.3rem);
}

@media (max-width: 760px) {
  .console-big {
    grid-template-columns: minmax(0, 1fr);
  }
}

.console-note {
  margin-block-start: 0.75rem;
  color: var(--text-dim);
}

@media (prefers-reduced-motion: reduce) {
  .console-scan {
    display: none;
  }
}
</style>
